import { fieldEffects, ruleRecord, unmetRequirements } from './fieldRules.js'
import { fieldLabel } from './labels.js'

/**
 * The fields a record must have and does not (D-343): those the company's field rules require of
 * it as it would be saved, and those its definition requires. Named as FieldInput names them:
 * 'name', 'account' (a reference), 'cf.region' (a custom field).
 *
 * `form`: the record as it would be saved, in the form's shape (toInputs: references by their
 * input key, `custom_fields`, `owner`); `original`: as stored, for rules that compare; `shown`:
 * names a dialog already shows (not asked again).
 */
export function missingFields(entity, form, meta, { original = null, shown = [] } = {}) {
  const effects = fieldEffects(meta.fieldRules(entity), ruleRecord(entity, form, meta), original ? ruleRecord(entity, original, meta) : null)
  const names = unmetRequirements(effects, form).map((key) => fieldName(entity, key, meta))
  for (const f of meta.fields(entity)) {
    if (!f.required || f.readonly || f.type === 'morph') continue
    const value = f.type === 'ref' ? form?.[f.input] : form?.[f.name]
    if (value === null || value === undefined || value === '') names.push(f.name)
  }
  return [...new Set(names)].filter((n) => !shown.includes(n))
}

/** A field's name as FieldInput takes it, from a key the server or the rules use ("custom_fields.region", "account_uuid"). */
export function fieldName(entity, key, meta) {
  if (key.startsWith('custom_fields.')) return `cf.${key.slice(14)}`
  return meta.fields(entity).find((f) => f.type === 'ref' && f.input === key)?.name || key
}

/** The fields named in a refused write's errors (FIELD_RULES_UNMET, a required one): FieldInput names. */
export function fieldsInErrors(entity, errors, meta) {
  const known = (name) => name.startsWith('cf.') ? !!meta.customField(entity, name.slice(3)) : !!meta.fields(entity).find((f) => f.name === name)
  return [...new Set(Object.keys(errors || {}).map((k) => fieldName(entity, k, meta)))].filter(known)
}

/** What the fields add to a write's body, from the inputs they were filled in. */
export function patchFor(entity, names, inputs, meta) {
  const patch = {}
  for (const name of names) {
    if (name.startsWith('cf.')) {
      patch.custom_fields = { ...(patch.custom_fields || {}), [name.slice(3)]: inputs.custom_fields?.[name.slice(3)] ?? null }
      continue
    }
    const field = meta.fields(entity).find((f) => f.name === name)
    if (field?.type === 'ref') patch[field.input] = inputs[field.input] ?? null
    else patch[name] = inputs[name] ?? null
  }
  return patch
}

/** A refused write's missing fields, by their labels ("Region, Segment"). */
export const missingLabels = (entity, names) => names.map((n) => fieldLabel(entity, n)).join(', ')
